import React, { useEffect, useState, useCallback, useMemo } from 'react'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { CaptionLG } from '@/atoms/Text'

/**
 * CountdownToMidnight Component
 *
 * This component displays a countdown timer that shows hours, minutes, and seconds
 * remaining until midnight in Mountain Standard Time (MST).
 *
 * Performance considerations:
 * - The time calculation function is memoized to prevent unnecessary recalculations
 * - We use a single interval for updates rather than multiple timers
 * - Error states are properly handled
 */

export interface CountdownToMidnightProps {
  onCountdownEnd?: () => void
  className?: string
  isDarkText?: boolean
}

export const CountdownToMidnight: React.FC<CountdownToMidnightProps> = ({ onCountdownEnd, className, isDarkText }) => {
  // Memoize the time calculation function to avoid recreating it on each render
  const getTimeRemainingUntilMidnightMST = useCallback(() => {
    try {
      // Get current time in MST using a more direct approach
      const now = new Date()
      const mstNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/Denver' }))

      // Create midnight time for today in MST
      const midnight = new Date(mstNow)
      midnight.setHours(23, 59, 59, 999)

      // Calculate time remaining
      const total = Math.max(0, midnight.getTime() - mstNow.getTime())
      const seconds = Math.floor((total / 1000) % 60)
      const minutes = Math.floor((total / 1000 / 60) % 60)
      const hours = Math.floor((total / (1000 * 60 * 60)) % 24)

      return {
        total,
        hours,
        minutes,
        seconds,
      }
    } catch (error) {
      // Fallback values in case of error with date calculations
      // Silent failure with fallback values is better than crashing the component
      return {
        total: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      }
    }
  }, [])

  // Get initial time values
  const initialTimeRemaining = useMemo(() => getTimeRemainingUntilMidnightMST(), [getTimeRemainingUntilMidnightMST])

  // Set up state for time tracking
  const [timeRemaining, setTimeRemaining] = useState(initialTimeRemaining)
  const [displayValues, setDisplayValues] = useState({
    hours: formatNumber(initialTimeRemaining.hours),
    minutes: formatNumber(initialTimeRemaining.minutes),
    seconds: formatNumber(initialTimeRemaining.seconds),
  })

  // Format numbers to always have two digits
  function formatNumber(num: number) {
    return num < 10 ? `0${num}` : `${num}`
  }

  // Update countdown every second
  useEffect(() => {
    // Define timer update function
    const updateTimer = () => {
      const newTimeRemaining = getTimeRemainingUntilMidnightMST()
      setTimeRemaining(newTimeRemaining)

      setDisplayValues({
        hours: formatNumber(newTimeRemaining.hours),
        minutes: formatNumber(newTimeRemaining.minutes),
        seconds: formatNumber(newTimeRemaining.seconds),
      })

      // Check if countdown has ended
      if (newTimeRemaining.total <= 0 && onCountdownEnd) {
        onCountdownEnd()
      }
    }

    // Update immediately
    updateTimer()

    // Then update every second
    const timerId = setInterval(updateTimer, 1000)

    // Clean up interval on unmount
    return () => clearInterval(timerId)
  }, [getTimeRemainingUntilMidnightMST, onCountdownEnd])

  // Don't render anything if countdown has ended
  if (timeRemaining.total <= 0) {
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={classNames('flex flex-col items-center md:flex-row md:items-center', className)}
    >
      <div className="mb-3 flex items-center md:mb-0 md:mr-4">
        <CaptionLG color={isDarkText ? 'black' : 'white'} className="md:photon-title-sm uppercase">
          <span className="flex items-center">
            <motion.span
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="mr-2 inline-block"
            >
              ⏱️
            </motion.span>
            ENDS IN
          </span>
        </CaptionLG>
      </div>

      <div className="flex items-center">
        {/* Hours */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mx-1 flex flex-col items-center"
        >
          <div className="min-w-[2.5rem] rounded-md bg-white/30 px-2 py-1 text-center backdrop-blur-sm">
            <span className={classNames('text-lg font-bold', isDarkText ? 'text-black' : 'text-white')}>
              {displayValues.hours}
            </span>
          </div>
        </motion.div>

        <span className="mx-0.5 text-xl font-bold text-white">:</span>

        {/* Minutes */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="mx-1 flex flex-col items-center"
        >
          <div className="min-w-[2.5rem] rounded-md bg-white/30 px-2 py-1 text-center backdrop-blur-sm">
            <span className={classNames('text-lg font-bold', isDarkText ? 'text-black' : 'text-white')}>
              {displayValues.minutes}
            </span>
          </div>
        </motion.div>

        <span className="mx-0.5 text-xl font-bold text-white">:</span>

        {/* Seconds */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="mx-1 flex flex-col items-center"
        >
          <motion.div
            className="min-w-[2.5rem] rounded-md bg-white/30 px-2 py-1 text-center backdrop-blur-sm"
            animate={{
              boxShadow: ['0 0 0 rgba(255,255,255,0)', '0 0 8px rgba(255,255,255,0.3)', '0 0 0 rgba(255,255,255,0)'],
            }}
            transition={{
              repeat: Infinity,
              duration: 1.5,
              ease: 'easeInOut',
            }}
          >
            <span className={classNames('text-lg font-bold', isDarkText ? 'text-black' : 'text-white')}>
              {displayValues.seconds}
            </span>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  )
}

export default CountdownToMidnight
